import S from 's-js';

// NOTE: Keep in sync w/ ./view.less (search "animation-duration").
const TAB_ANIMATION_DURATION_MS = 500;

/**
 * A tab type that treats the tab's title as a viewmodel concern. If all you need is text titles for your tabs,
 * then this is a convenient pattern b/c it keeps tab declarations and title declarations in the same place.
 */
export interface TitledTab {
    /**
     * Title of this tab displayed in the UI.
     */
    readonly Title : string;
}

export interface TabStateSettledOnFront {
    readonly state : 'settled-on-front';
}

export interface TabStateSettledOnBack<TTab> {
    readonly state : 'settled-on-back';
    readonly tab : TTab;
}

export interface TabStateFlippingToFront<TTab> {
    readonly state : 'flipping-to-front';
    readonly fromTab : TTab;
}

export interface TabStateFlippingToBack<TTab> {
    readonly state : 'flipping-to-back';
    readonly toTab : TTab;
}

export type TabState<TTab> =
    TabStateSettledOnFront
  | TabStateSettledOnBack<TTab>
  | TabStateFlippingToFront<TTab>
  | TabStateFlippingToBack<TTab>;

export function TabStateEq<TTab>(state1 : TabState<TTab>, state2 : TabState<TTab>) {
    return (
        state1.state === 'settled-on-front'  && state2.state === 'settled-on-front'
     || state1.state === 'settled-on-back'   && state2.state === 'settled-on-back'   && state1.tab     === state2.tab
     || state1.state === 'flipping-to-front' && state2.state === 'flipping-to-front' && state1.fromTab === state2.fromTab
     || state1.state === 'flipping-to-back'  && state2.state === 'flipping-to-back'  && state1.toTab   === state2.toTab
    );
}

export interface PCTabStrip<TTab> {
    FrontTab : TTab;
    BackTabs : () => readonly TTab[];
    State : () => TabState<TTab>;
    FlipToTab : (tab : TTab) => void;
}

/**
 * Construct a new `PCTabStrip` instance.
 * @param FrontTab The tab instance representing the front tab.
 * @param BackTabs A function that returns an array of tab instances representing back tabs that should be
 * displayed as clickable buttons to the user, in presentation order. You can also programmatically navigate
 * to tabs other than those specified here by calling the `FlipToTab` method.
 */
export function PCTabStrip<TTab>(
    FrontTab : TTab,
    BackTabs : () => readonly TTab[],
    initState : TabState<TTab> = { state: 'settled-on-front' }
) : PCTabStrip<TTab> {
    const
        State = S.value(initState, TabStateEq),
        CheckedBackTabs = S(() => {
            const
                known = new Set<TTab>([FrontTab]),
                backTabs = BackTabs();

            let valid = true;
            for (let i = 0; i < backTabs.length; i++) {
                if (known.has(backTabs[i])) {
                    valid = false;
                    console.warn(`Back tab at index ${i} equals the front tab or another back tab. As a result, this PCTabStrip won't render any back tab buttons.`);
                }
                known.add(backTabs[i]);
            }
            return valid ? backTabs : [];
        });

    S(() => {
        // If in one of the flipping states, set a timeout to switch to one of the settled states after an
        // appropriate delay.
        const s = State();
        if (s.state === 'flipping-to-front' || s.state === 'flipping-to-back') {
            const
                nextState : TabState<TTab> = s.state === 'flipping-to-front' ? { state: 'settled-on-front' } : { state: 'settled-on-back', tab: s.toTab },
                timeoutId = setTimeout(() => State(nextState), TAB_ANIMATION_DURATION_MS);
            S.cleanup(() => clearTimeout(timeoutId));
        }
    });

    return {
        FrontTab,
        BackTabs: CheckedBackTabs,
        State: () => State(),
        FlipToTab
    };

    function FlipToTab(tab : TTab) {
        const s = S.sample(State);
        if (tab === FrontTab) {
            if (s.state === 'settled-on-front') {
                // Nothing to do. We're already there.
            } else if (s.state === 'flipping-to-front') {
                // Also nothing to do. We're already on our way.
            } else if (s.state === 'flipping-to-back') {
                // We were flipping to the back but got a contradictory instruction to flip to the front before
                // we even made it there. Say we're flipping from whatever back tab we were trying to get to.
                State({ state: 'flipping-to-front', fromTab: s.toTab });
            } else if (s.state === 'settled-on-back') {
                State({ state: 'flipping-to-front', fromTab: s.tab });
            }
        } else {
            // Treat any other tab as a back tab, whether it's known to us already or not.
            if (s.state === 'settled-on-back') {
                // Already on a back tab (might be same or might be different) -- say we're on the new back tab
                // w/o going through our flip animation. Jumping straight from one back tab to another isn't
                // something our tab strip UI currently allows but IS something calling code might choose to do
                // by calling this FlipToTab method itself.
                State({ state: 'settled-on-back', tab });
            }  else {
                // Otherwise, whatever was going on before, just say we're now flipping to the specified back tab.
                State({ state: 'flipping-to-back', toTab: tab });
            }
        }
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/pctabstrip/model.ts